import React, { useState, useEffect, useRef } from 'react'
import CommentEditor from './CommentEditor'
import Comment from './Comment'
import { Flex, ResponsiveSVG } from 'ui/acro'
import Chevron from '../../../assets/images/chevron.svg'
import styled from 'styled-components'
import Reply from './Reply'
import commentAxios from 'api/commentsAxios'
import { Spinner } from 'ui/bend'

const RepliesContainer = styled(Flex)`
  flex-direction: column;
  .replies-expand {
    transform: rotate(90deg);
  }
  .replies-collapse {
    transform: rotate(270deg);
  }
`

const Toggle = styled(Flex)`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  gap: 4px;
  margin-bottom: 12px;
  cursor: pointer;
  width: 100px;
`
const ShowMore = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  cursor: pointer;
  width: 150px;
`
const SpinnerContainer = styled.div`
  width: 200px;
  margin-top: 32px;
`

// Comment Display Component
const CommentWithReplies = ({
  comment,
  onReply,
  onEdit,
  onDelete,
  isEditing,
  onSaveEdit,
  onCancelEdit,
  activeReplyId,
  setActiveReplyId,
  type,
  id,
  userName,
  setComments,
  setShowSnackbar,
  user,
  invokeGA4Event
}) => {
  const [showReplies, setShowReplies] = useState(false)
  const [replies, setReplies] = useState([])
  const [load, setLoad] = useState(false)
  const [repliesLoading, setRepliesLoading] = useState(false)
  const [nextPageLoading, setNextPageLoading] = useState(false)
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null)
  const [editingReplyId, setEditingReplyId] = useState(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (showReplies) {
      fetchReplies(null)
    }
  }, [showReplies])

  useEffect(() => {
    if (isFirstRender.current) {
      // Skip running on initial render
      isFirstRender.current = false
      return
    }
    if (showReplies) {
      fetchReplies(null)
    } else {
      setShowReplies(true)
    }
  }, [comment?.replyCount])

  const fetchReplies = async (lastEvaluatedItem) => {
    try {
      const response = await commentAxios.post('/replies', {
        content_type: type,
        content_id: id,
        comment_id: comment?.commentId,
        limit: 10,
        last_evaluated_key: lastEvaluatedItem
      })
      if (response.status === 200) {
        setRepliesLoading(false)
        setNextPageLoading(false)
        if (lastEvaluatedItem) {
          setReplies((prevReplies) =>
            prevReplies?.concat(response?.data?.replies)
          )
        } else {
          setReplies(response?.data?.replies)
        }
        setLastEvaluatedKey(response?.data?.lastEvaluatedKey)
      }
    } catch (err) {
      console.log(err)
    }
  }
  const incrementPageNumber = () => {
    if (lastEvaluatedKey) {
      setNextPageLoading(true)
      fetchReplies(lastEvaluatedKey)
    }
  }

  const deleteReply = async (reply_id) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.post('/content-interaction/delete', {
        content_type: type,
        content_id: id,
        comment_id: comment?.commentId,
        reply_id: reply_id
      })
      if (response.status === 200) {
        // if (editingId === id) setEditingId(null)
        setLastEvaluatedKey(null)
        setComments((prev) =>
          prev.map((individualComment) =>
            individualComment.commentId === comment.commentId
              ? {
                  ...individualComment,
                  replyCount: comment.replyCount - 1
                }
              : individualComment
          )
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  const updateReply = async (updatedText) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.put('/content-interaction', {
        content_type: type,
        content_id: id,
        text: updatedText,
        comment_id: comment?.commentId,
        reply_id: editingReplyId
      })
      if (response.status === 200) {
        setReplies((prev) =>
          prev.map((reply) =>
            reply.replyId === editingReplyId
              ? {
                  ...reply,
                  text: updatedText
                }
              : reply
          )
        )
        setEditingReplyId(null)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  return (
    <div style={{ marginBottom: '48px' }}>
      {isEditing ? (
        <CommentEditor
          mode='edit'
          initialValue={comment.text}
          onSubmit={onSaveEdit}
          onCancel={onCancelEdit}
          comment={comment}
        />
      ) : (
        <Comment
          comment={comment}
          onEdit={onEdit}
          onDelete={onDelete}
          setActiveReplyId={setActiveReplyId}
          activeReplyId={activeReplyId}
          type={type}
          id={id}
          userName={userName}
          setComments={setComments}
          setShowReplies={setShowReplies}
          setShowSnackbar={setShowSnackbar}
          invokeGA4Event={invokeGA4Event}
        />
      )}

      {activeReplyId === comment.commentId && !isEditing && (
        <CommentEditor
          mode='reply'
          onSubmit={(text) => {
            onReply(comment.commentId, text)
            setActiveReplyId(null)
          }}
          onCancel={() => setActiveReplyId(null)}
          comment={comment}
          user={user}
        />
      )}

      {comment.replyCount > 0 && (
        <RepliesContainer mb='20px'>
          <Toggle
            onClick={() => {
              !showReplies && setRepliesLoading(true)
              setShowReplies(!showReplies)
              setLastEvaluatedKey(null)
            }}
          >
            <ResponsiveSVG
              SVG={Chevron}
              style={{
                cursor: 'pointer'
              }}
              width='20px'
              height='20px'
              className={showReplies ? 'replies-expand' : 'replies-collapse'}
            />
            {comment.replyCount}{' '}
            {comment.replyCount === 1 ? 'Reply' : 'Replies'}
          </Toggle>
          {repliesLoading && (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          )}

          {showReplies && (
            <Flex
              flexDirection='column'
              gap='24px'
              style={{ marginLeft: '20px' }}
            >
              {replies.map((reply) =>
                editingReplyId === reply?.replyId ? (
                  <CommentEditor
                    mode='reply'
                    initialValue={reply.text}
                    onSubmit={updateReply}
                    onCancel={() => setEditingReplyId(null)}
                    comment={reply}
                    user={user}
                  />
                ) : (
                  <Reply
                    key={reply.replyId}
                    reply={reply}
                    commentId={comment?.commentId}
                    id={id}
                    type={type}
                    userName={userName}
                    setShowSnackbar={setShowSnackbar}
                    setReplies={setReplies}
                    onDelete={deleteReply}
                    onEdit={(id) => {
                      setEditingReplyId(id)
                    }}
                  />
                )
              )}

              {replies.length > 0 && lastEvaluatedKey && !nextPageLoading && (
                <ShowMore onClick={() => incrementPageNumber()}>
                  Show More Replies
                </ShowMore>
              )}
              {nextPageLoading && (
                <SpinnerContainer>
                  <Spinner />
                </SpinnerContainer>
              )}
            </Flex>
          )}
        </RepliesContainer>
      )}
    </div>
  )
}

export default CommentWithReplies
